import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';

import type {
  ExportTemplateColumn,
  ExportTemplateDefinition,
  ExportTemplatePreview,
  ExportTransformation,
} from '../../types/export.types';
import { buildPreviewCells, specWarningFor } from '../../utils/export-preview-model';

import { PreviewRowDetail } from './preview-row-detail';

interface PreviewFileViewProps {
  preview: ExportTemplatePreview;
  /** Column key → its config entry (label, kind, spec limits). */
  columnsByKey: Record<string, ExportTemplateColumn>;
  /** Column keys the active template encodes — marked in the header. */
  encodedKeys: Set<string>;
  /** Spec limits only mean something once an encoding is applied: a raw export
   *  reproduces stored values, which the vendor never sees through this file. */
  showSpecWarnings: boolean;
  /** The active template — supplies the per-column encoding for an expanded row. */
  template: ExportTemplateDefinition | undefined;
  /** Transformation code → definition, for the encoding label on an expanded row. */
  transformationsByCode: Record<string, ExportTransformation>;
  /** Header keys to show. The server always emits the internal `id` whatever
   *  `fields` asks for, so the grid hides what was not selected rather than
   *  showing a column the operator deselected. Undefined shows everything. */
  visibleKeys?: ReadonlySet<string>;
  /**
   * Opts into a self-contained, bounded scroll box (horizontal AND vertical, capped at a sane
   * viewport fraction) instead of the drawer-only default below. Every existing caller renders
   * this inside a Radix `Sheet` — which already owns the page's one vertical scroller, wraps its
   * content in `react-remove-scroll`, and would have a wheel event over a second inner scroller
   * swallowed instead of moving the sheet — so the default stays exactly as it was. The catalog
   * builder's "File preview" tab is not a sheet; it is a plain tab panel with no outer scroller of
   * its own, so it opts in here instead of forking this view. Defaults to `false`.
   */
  boundedHeight?: boolean;
}

/**
 * The export as a file: header row plus one row per sampled product, in the
 * connector's own column order. This is the view that answers "what will I
 * actually open in a spreadsheet" — the stored-vs-exported diff answers a
 * different question and lives in its own tab.
 *
 * Wide by nature (a connector can define 52 columns), so it scrolls
 * horizontally inside its own container with a sticky header row and a sticky
 * first column. That first column is the connector's identifier, which is what
 * an operator scans by, so pinning it keeps every row identifiable at any
 * horizontal scroll offset.
 *
 * Cells stay clipped to keep row heights uniform and the grid scannable, so any
 * row can be expanded into `PreviewRowDetail` — every column of that one
 * product, wrapped and in full. That is the readable path for the values this
 * grid necessarily clips; one row is open at a time, because the panel is as
 * tall as the connector has columns.
 */
export function PreviewFileView({
  preview,
  columnsByKey,
  encodedKeys,
  showSpecWarnings,
  template,
  transformationsByCode,
  visibleKeys,
  boundedHeight = false,
}: PreviewFileViewProps) {
  const { t } = useTranslation('exports');
  // Index of the expanded row, or `null`. One at a time: the panel lists every
  // column, so two open at once buries the grid it belongs to.
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  // Spec limits are measured once per response, not per render. The grid is
  // rows x columns (up to 25 x 52) and every check scans the value — one of them
  // splits it on commas — so recomputing inside the render loop cost ~1300
  // string scans on each paint, including a hover that only changes a tint.
  // The header positions on screen. `columnIndex` stays the position in the
  // response, which is what `raw`/`encoded` are indexed by; the first VISIBLE
  // one is the sticky identifier column.
  const visibleColumns = useMemo(
    () =>
      preview.header
        .map((key, columnIndex) => ({ key, columnIndex }))
        .filter(({ key }) => visibleKeys === undefined || visibleKeys.has(key)),
    [preview.header, visibleKeys],
  );

  // Parallel to `preview.rows`: warningsByRow[r][c] pairs with header[c].
  const warningsByRow = useMemo(
    () =>
      preview.rows.map((row) =>
        preview.header.map((key, columnIndex) =>
          showSpecWarnings
            ? specWarningFor(row.encoded[columnIndex] ?? '', columnsByKey[key])
            : null,
        ),
      ),
    [preview, columnsByKey, showSpecWarnings],
  );

  // Only the open row is flattened — building all 25 products' cells up front
  // would do 25x the work to render one panel.
  const expandedCells = useMemo(
    () =>
      expandedRow === null
        ? []
        : buildPreviewCells({
            preview,
            rowIndex: expandedRow,
            template,
            columnsByKey,
            transformationsByCode,
          }),
    [expandedRow, preview, template, columnsByKey, transformationsByCode],
  );

  return (
    // Default (`boundedHeight` false): horizontal scrolling only, and no height cap — see the
    // prop doc for why every sheet caller needs exactly this. `boundedHeight` swaps that for a
    // self-contained box: bounded at a viewport fraction, scrolling both ways, with its own
    // `flex-1` since there is no outer scroller to defer the vertical axis to.
    <div
      data-testid="export-preview-file"
      className={cn(
        'rounded-md border',
        boundedHeight ? 'max-h-[70vh] min-h-0 flex-1 overflow-auto' : 'overflow-x-auto',
      )}
    >
      {/* A real <table>, not a grid: this IS tabular data, so screen readers get
          row/column semantics for free. `border-separate` with zeroed spacing is
          what lets the sticky header and sticky first column keep their borders
          while translated — collapsed borders drop out from under sticky cells. */}
      <table
        data-testid="export-preview-file-table"
        className="w-max border-separate border-spacing-0 text-xs"
      >
        <caption className="sr-only">{t('preview.file.caption')}</caption>
        <thead>
          <tr>
            {visibleColumns.map(({ key }, position) => {
              const column = columnsByKey[key];
              const encoded = encodedKeys.has(key);
              return (
                <th
                  key={key}
                  data-testid={`export-preview-file-column-${key}`}
                  scope="col"
                  className={cn(
                    'bg-selected-row sticky top-0 z-20 border-r border-b px-3 py-2 text-left align-middle font-semibold',
                    // The identifier column is pinned too, so its header cell
                    // needs a higher stacking order than the rest of the row.
                    position === 0 && 'left-0 z-30',
                  )}
                >
                  <span className="text-foreground block whitespace-nowrap">
                    {column?.label ?? key}
                  </span>
                  {/* Always two lines — the marker sits beside the key — so every
                      header is the same height and the labels line up whether or
                      not the template rewrites a column. Same treatment as the
                      full-page view. */}
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    <span className="text-muted-foreground font-mono font-normal">{key}</span>
                    {/* Encoding is marked with a text glyph AND a cell tint, never
                        the tint alone — colour is not an accessible carrier by itself. */}
                    {encoded && (
                      <span className="text-accent-main text-[10px] font-semibold">
                        {t('preview.file.encodedMarker')}
                      </span>
                    )}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {preview.rows.map((row, rowIndex) => {
            const isExpanded = expandedRow === rowIndex;
            // E2E key: the row's identifier cell (stable product id), index only as a fallback.
            const rowTestKey = row.encoded[0] ?? String(rowIndex);
            return (
              // Keyed on the row's identifier cell (the connector's first column)
              // with the index as a tiebreak — the sample is ordered and immutable,
              // but two rows sharing an id must still not collide.
              <Fragment key={`${row.encoded[0] ?? ''}-${String(rowIndex)}`}>
                <tr data-testid={`export-preview-file-row-${rowTestKey}`} className="group">
                  {visibleColumns.map(({ key, columnIndex }, position) => {
                    const value = row.encoded[columnIndex] ?? '';
                    const warning = warningsByRow[rowIndex]?.[columnIndex] ?? null;
                    const isIdentifierColumn = position === 0;
                    return (
                      <td
                        key={key}
                        data-testid={`export-preview-file-cell-${rowTestKey}-${key}`}
                        className={cn(
                          'max-w-[280px] truncate border-r border-b px-3 py-2 align-top font-mono',
                          isIdentifierColumn
                            ? 'bg-background group-hover:bg-selected-row sticky left-0 z-10'
                            : cn(
                                'group-hover:bg-selected-row/50',
                                encodedKeys.has(key) && 'bg-success-50/40',
                              ),
                          // Warning tint wins over the encoded tint — an over-limit
                          // cell is the more urgent of the two facts.
                          warning && 'bg-warning-50 text-foreground',
                        )}
                        // Full value on hover: cells are truncated to keep the row
                        // height uniform, so this is the only way to read a long one.
                        title={value || undefined}
                      >
                        <span className="flex items-center gap-1">
                          {/* The disclosure lives in the identifier cell rather than
                          in a column of its own: that cell is already the sticky
                          one, so a dedicated control column would have to
                          re-derive every sticky offset below it. */}
                          {isIdentifierColumn && (
                            <button
                              type="button"
                              className="text-muted-foreground hover:text-foreground -my-0.5 shrink-0 cursor-pointer rounded p-0.5"
                              aria-expanded={isExpanded}
                              data-testid={`export-preview-file-row-expand-button-${rowTestKey}`}
                              aria-controls={`export-preview-row-${String(rowIndex)}`}
                              aria-label={
                                isExpanded
                                  ? t('preview.detail.collapse')
                                  : t('preview.detail.expand')
                              }
                              onClick={() => {
                                setExpandedRow(isExpanded ? null : rowIndex);
                              }}
                            >
                              {isExpanded ? (
                                <ChevronDown aria-hidden="true" className="size-3.5" />
                              ) : (
                                <ChevronRight aria-hidden="true" className="size-3.5" />
                              )}
                            </button>
                          )}
                          {warning && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <AlertTriangle
                                  className="text-warning size-3 shrink-0 cursor-help"
                                  aria-label={t('preview.spec.overLimitAria')}
                                />
                              </TooltipTrigger>
                              <TooltipContent>
                                {warning.kind === 'length'
                                  ? t('preview.spec.length', {
                                      actual: warning.actual,
                                      limit: warning.limit,
                                    })
                                  : t('preview.spec.repetitions', {
                                      actual: warning.actual,
                                      limit: warning.limit,
                                    })}
                              </TooltipContent>
                            </Tooltip>
                          )}
                          {/* An empty cell renders empty, not as a dash: the file
                          being previewed has nothing there, and a glyph would
                          read as a value the export does not contain. */}
                          <span className="truncate">{value}</span>
                        </span>
                      </td>
                    );
                  })}
                </tr>
                {isExpanded && (
                  <tr>
                    {/* Spans the whole grid so the panel gets the drawer's full
                    width no matter how far the grid is scrolled sideways. */}
                    <td
                      id={`export-preview-row-${String(rowIndex)}`}
                      colSpan={visibleColumns.length}
                      className="bg-muted/30 border-b p-0"
                    >
                      <div className="sticky left-0 w-[min(100%,72rem)]">
                        <PreviewRowDetail
                          cells={expandedCells}
                          testId={`export-preview-row-detail-${rowTestKey}`}
                        />
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
